/* eslint-disable max-lines */
/* eslint-disable complexity */
import { bindLDAP } from '@adornis/authentication/ldap/server/ldap-internals.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchEntireCollection, watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Materialize } from '@adornis/materialized-views/decorators/materialize.js';
import { createStripeCustomer } from '@adornis/payments/server/customer.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import type { UserPermission } from '@adornis/users/db/types.js';
import { getPasswordResetLink } from '@adornis/users/server/password.js';
import { createNewUser } from '@adornis/users/server/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { map, switchMap } from 'rxjs';
import { Permission, Role } from '../enums/roles.js';
import { UserNotFoundError } from '../errors/UserNotFoundError.js';
import { ldapImplementations } from '../ldap.js';
import { SuspensionLog } from '../logs/SuspensionLog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { DynamicAdornisRoleToContextsWrapper } from './DynamicAdornisRoleToContextsWrapper.js';
import { Participation } from './Participation.js';
import { ActionTokenData } from './action.js';
import { Institution } from './institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from './mail-templates.js';
import { UserStammdaten } from './stammdaten.js';
import { Suspension } from './suspension.js';
import { Workshop } from './workshop.js';

@Materialize({
  selectionSet: async () => {
    const { Suspension } = await import('./suspension.js');

    return {
      email: 1,
      username: 1,
      title: 1,
      firstName: 1,
      lastName: 1,
      createdAt: 1,
      participations: {
        _id: 1,
        userID: 1,
        productID: 1,
        product: {
          _id: 1,
          name: 1,
          institutionID: 1,
          timeframeID: 1,
          hasPrice: 1,
          prices: { unitAmount: 1, currency: 1 },
        },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: {
          status: 1,
          participationIDs: 1,
          orderNr: { year: 1, nr: 1 },
          amount: 1,
          discount: 1,
        },
        productPriceForUser: { unitAmount: 1, currency: 1 },
        userStammdaten: selectionSet(() => UserStammdaten, 3),
      },
      isLdapAccount: 1,
      stripeCustomerID: 1,
      suspentions: selectionSet(() => Suspension, 1),
      whatsappNotification: 1,
      whatsappPhone: 1,
      receivesNewsletter: 1,
    };
  },
})
@Entity()
export class User extends AdornisUser<Permission | UserPermission> {
  static override _class = 'User';

  constructor(doc: PartialEntityData<User>) {
    super(doc);
  }

  @Field(type => String)
  title?: string;

  @validate(nonOptional())
  @Field(type => String)
  firstName!: string;

  @validate(nonOptional())
  @Field(type => String)
  lastName!: string;

  @DataLoadedField(type => [Participation], ({ _id }) => ({ userID: _id }))
  participations!: Participation[];

  //! this is necessary to show stammdaten in the user-export-view
  @Field(type => UserStammdaten, {
    resolve: function (this: User) {
      return async gqlFields => {
        if (this.stammdaten) return this.stammdaten; // do not refetch
        const institutionID = (context as any).institutionID;
        if (!institutionID) {
          // TODO: remove <Entity>.allFields from codebase, so that this works with the error is place
          return null; // throwing an error breaks to much stuff right now
          throw new Error('this fiel should only be requsted when the context includes an institutionID');
        }
        const stammdaten = await UserStammdaten.getExistingStammdaten(this._id, institutionID, null)(gqlFields);
        return stammdaten;
      };
    },
  })
  stammdaten: Maybe<UserStammdaten>;

  //! this is necessary to show suspended status in the admin-institution-particpants view
  @Field(type => Boolean, {
    resolve: function (this: User) {
      return async gqlFields => {
        const institutionID = (context as any).institutionID;
        logger.info({ institutionID: institutionID ?? 'empty', suspentions: this.suspentions });
        if (!institutionID) return null;
        return !!this.suspentions?.find(s => s.institutionID === institutionID);
      };
    },
  })
  suspended: Maybe<boolean>;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  isLdapAccount!: boolean;

  @Field(type => String)
  stripeCustomerID?: string;

  @validate(nonOptional())
  @Field(type => [Suspension], { default: v => v ?? [] })
  suspentions!: Suspension[];

  @Field(type => Boolean, { default: v => v ?? false })
  whatsappNotification!: boolean;

  @Field(type => String)
  whatsappPhone: Maybe<string>;

  @Field(type => Boolean, { default: v => v ?? false })
  receivesNewsletter!: boolean;

  get isSuperAdmin() {
    return !!this.roles.find(role => role.name === Role.SUPER_ADMIN);
  }

  get isAdmin() {
    return !!this.roles.find(role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN);
  }

  get adminRole() {
    return this.roles.find(role => role.name === Role.ADMIN);
  }

  get isBerater() {
    return !!this.roles.find(role => role.name === Role.BERATER);
  }

  get isParticipant() {
    return !!this.roles.find(role => role.name === Role.PARTICIPANT);
  }

  get isLecturer() {
    return !!this.roles.find(role => role.name === Role.LECTURER);
  }

  get lecturerRole() {
    return this.roles.find(role => role.name === Role.LECTURER);
  }

  static override async afterUpdate(oldEntity: User, update: User): Promise<void> {
    // @ts-expect-error this is only not optional so that the DX doesn't get confused
    delete oldEntity.password;
  }

  static override async beforeCreate(entity: User): Promise<void> {
    const collection = await getRawCollection<User>(this._collectionName);
    const count = await collection.countDocuments({
      email: { $regex: `^${entity.email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' },
    });
    if (count > 0) throw new Error('Ein Nutzer mit dieser E-Mail existiert bereits.');

    return super.beforeCreate(entity);
  }

  static async getWorkshopsForUser(user: User) {
    const institutionIds = user.roles.filter(r => r.name === Role.ADMIN).flatMap(r => r.contexts);
    let workshops: Workshop[] = [];
    for (const institutionID of institutionIds) {
      const results = await Workshop.getAllWorkshopsForInstitution(institutionID)({});
      workshops = workshops.concat(results);
    }
    return workshops;
  }

  static async getParticipantIdsForWorkshops(workshops: Workshop[]) {
    const col = await getCollection<Participation>(Participation._class);
    const participations = await col.find<Participation>({ productID: { $in: workshops.map(w => w._id) } }).toArray();
    return participations;
  }

  static async ensureSelfRoles(userID: string) {
    const user = await User.getByID<User>(userID)({
      _id: 1,
      roles: AdornisRoleToContextsWrapper.allFields,
    });

    if (!user) return;

    // We want to ensure that everyone has a User role on it's own context.
    // Otherwise you can't change your own Password.
    if (!user.hasPermission(Permission.ChangePassword, user._id)) {
      user.roles.push(
        new AdornisRoleToContextsWrapper({
          name: Role.USER,
          contexts: [user._id],
        }),
      );

      // write roles on user
      await (
        await getCollection<User>(User._class)
      ).updateOne(
        { _id: user._id },
        {
          $set: {
            roles: user.roles.map(r => r.toJSON() as EntityData<AdornisRoleToContextsWrapper>),
          },
        },
      );
    }
  }

  static async ensureDynamicRoles(userID: string) {
    const user = await getByID(
      User,
      userID,
    )({ _id: 1, roles: AdornisRoleToContextsWrapper.allFields, suspentions: { institutionID: 1 } });

    if (!user) return;

    // remove all existing dynamic roles
    user.roles = user.roles.filter(role => !(role instanceof DynamicAdornisRoleToContextsWrapper));

    // add new dynamic roles
    const dynamicRoles: DynamicAdornisRoleToContextsWrapper[] = [];

    const { Einzelberatung } = await import('./einzelberatung.js');
    const beraterRoles = await Einzelberatung.getEinzelberatungByBeraterID(userID)(Einzelberatung.allFields);
    const beratungenIDs = beraterRoles.map(beratung => beratung._id);
    const suspensionIDs = user.suspentions.map(suspension => suspension.institutionID);
    const institutionIDs = beraterRoles
      .map(beratung => beratung.institutionID)
      .filter(id => !suspensionIDs.includes(id));

    if (beratungenIDs.length > 0) {
      dynamicRoles.push(
        new DynamicAdornisRoleToContextsWrapper({
          name: Role.BERATER,
          contexts: [...beratungenIDs, ...institutionIDs],
        }),
      );
    }

    // write roles on user
    await (
      await getCollection<User>(User._class)
    ).updateOne(
      { _id: user._id },
      {
        $set: {
          roles: [...user.roles, ...dynamicRoles].map(r => r.toJSON() as EntityData<AdornisRoleToContextsWrapper>),
        },
      },
    );
  }

  @Subscription(type => [User])
  static subscribeAllUsersWithoutParticipation(@Arg('workshopID', type => String) workshopID: string) {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      return watchEntireCollection(`${User._collectionName}-materialized`).pipe(
        switchMap(async () => {
          const collection = await getRawCollection(`${User._collectionName}-materialized`);
          return collection
            .find({ participations: { $not: { $elemMatch: { product: { _id: workshopID } } } } })
            .toArray();
        }),
      );
    };
  }

  @Subscription(type => [User])
  static subscribeAllNonAdmins() {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      const userHandle = getCollectionHandle<User>(User._collectionName);

      return userHandle.watchQuery({
        roles: {
          $not: {
            $elemMatch: {
              name: { $in: [Role.ADMIN, Role.SUPER_ADMIN] },
            },
          },
        },
      });
    };
  }

  @Mutation(type => String)
  static cancelSuspensionFromInstitution(
    @Arg('userID', type => String) userID: string,
    @Arg('institutionID', type => String) institutionID: string,
  ) {
    return async () => {
      const user = await User.getByID<User>(userID)({ suspentions: Suspension.allFields });

      if (!user) throw new Error('User not found');

      const suspentionIndex = user.suspentions.findIndex(suspention => suspention.institutionID === institutionID);
      const removedSuspension = user.suspentions.splice(suspentionIndex, 1);

      await user.save();

      await Participation.cancelSuspensionParticipationsByUserInstitution(userID, institutionID);

      const eventLog = new SuspensionLog({
        locked: false,
        suspension: removedSuspension[0],
        userID,
      });
      await eventLog.create();
    };
  }

  @Mutation(type => String)
  static suspendUserFromInstitution(
    @Arg('userID', type => String) userID: string,
    @Arg('suspension', type => Suspension) suspension: Suspension,
  ) {
    return async () => {
      const user = await User.getByID<User>(userID)({ suspentions: Suspension.allFields });

      if (!user) throw new Error('User not found');

      if (user.suspentions.map(suspention => suspention.institutionID).includes(suspension.institutionID))
        throw new Error('Dieser Nutzer ist bereits suspendiert!');

      user.suspentions.push(suspension);
      await user.save();

      await Participation.suspendParticipationsByUserInstitution(userID, suspension.institutionID);

      const eventLog = new SuspensionLog({
        locked: true,
        suspension,
        userID,
      });
      await eventLog.create();
    };
  }

  @Mutation(type => Boolean)
  static useAction(@Arg('action', type => ActionTokenData) action: ActionTokenData) {
    return async () => {
      if (!action.isValid) throw new Error('action is not valid');

      switch (action.action) {
        case 'confirm-mail':
          const user = await getByID(User, action.userID)({ emailConfirmed: 1 });
          if (!user) throw new Error('User not found');
          user.emailConfirmed = true;
          await user.save();
          break;
      }

      action.isValid = false;
      await action.save();
      return true;
    };
  }

  @Mutation(type => Boolean)
  static loginWithLDAP(
    @Arg('username', type => String) email: string,
    @Arg('password', type => String) password: string,
  ) {
    // eslint-disable-next-line max-statements
    return async () => {
      if (context.serverContext) throw new Error('the server does not need to login with ldap!');
      const config = await import('../config.js').then(m => m.config);
      const implementation = ldapImplementations[config.get('LDAP_IMPLEMENTATION')];
      const filter = implementation.getFilter(email);
      const ldapResult = await bindLDAP(filter, password);

      const { email: ldapEmail, firstName, lastName } = implementation.getUserData(email, ldapResult);

      const collection = await getRawCollection<User>(this._collectionName);
      const selector = { $regex: `^${email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' };
      const existingUser = await collection.findOne<User>({ $or: [{ username: selector }, { email: selector }] });

      let userID = '';
      if (!existingUser) {
        const user = new User({
          username: ldapEmail,
          email: ldapEmail,
          emailConfirmed: true,
          isLdapAccount: true,
          firstName,
          lastName,
        });
        userID = await createNewUser(user, undefined, true);
      } else {
        if (!existingUser.isLdapAccount) {
          await collection.updateOne(
            { $or: [{ username: selector }, { email: selector }] },
            { $set: { isLdapAccount: true } },
          );
        }

        // await changeUserPassword(existingUser._id, password); // ?? not sure if we can do that, looks debatable
        userID = existingUser._id;
      }

      context.userID = userID;
      context.userClass = User._class;
      context.impersonatingUser = undefined;
      context.impersonatingUserClass = undefined;
      await context.recreate();

      return true;
    };
  }

  @Query(type => User)
  static getUserByEmail(@Arg('email', type => String) email: string) {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      const collection = await getCollection<User>(User._class);
      const result = await collection.findOne<User>({ email });
      return result;
    };
  }

  @Query(type => Boolean, { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } })
  static getUserByEmailExists(@Arg('email', type => String) email: string) {
    return async () => {
      const userCol = await getRawCollection<EntityData<User>>(User._collectionName);
      const existingUserByEmail = await userCol.findOne({
        email: { $regex: `^${email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' },
      });
      return !!existingUserByEmail;
    };
  }

  @Mutation(type => String)
  static sendLoginLink(
    @Arg('email', type => String) email: string,
    @Arg('institutionID', type => String) institutionID: string,
  ) {
    return async () => {
      const user = await (
        await getRawCollection<EntityData<User>>(User._collectionName)
      ).findOne(
        { email: { $regex: `^${email.trim().replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' } },
        { projection: { password: 0 } },
      );
      if (!user) throw new UserNotFoundError();

      const link = await getPasswordResetLink('/reset-password', {
        userID: user._id,
        userClass: user._class,
        ttu: 1 /** Times to use. How often the token can be used */,
        ttl: 30 * 60 * 1000 /** Time to live. How long the token is valid in ms */,
      });

      const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
        institutionID,
        MailTemplateType.FORGOT_PASSWORD,
      )(InstitutionMailTemplate.allFields);

      if (!mailTemplate.emailNotification) return;

      const mailContent = await mailTemplate.buildEmail({
        [MailTemplateVars.TITLE]: user.title ?? '',
        [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
        [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
        [MailTemplateVars.FORGOT_PASSWORD_LINK]: InstitutionMailTemplate.mapForgotPassword(link),
      } as any);

      if (!mailContent) throw new Error("content couldn't be found.");

      await sendInstitutionEmail({
        email: user.email,
        subject: mailTemplate.subject,
        content: mailContent,
        institutionID,
      });
    };
  }

  @Query(type => [User])
  static getAllNoneAdmins() {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      const collection = await getCollection<User>(User._class);
      const result = await collection
        .find<User>({
          'roles.name': {
            $nin: [Role.ADMIN, Role.SUPER_ADMIN],
          },
        })
        .toArray();
      return result;
    };
  }

  @Subscription(type => [User])
  static subscribeAdmins(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => AdornisFilter) filter: AdornisFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      const filterObject = filter.toObject();
      if (filterObject.search) filterObject.search = filterObject.search.replace(/([()[{*+.$^\\|?])/g, '\\$1');

      const userFilter = {
        'roles.name': {
          $in: [Role.ADMIN, Role.SUPER_ADMIN],
        },
      };

      const sorting = {};
      filter.sortingFields.forEach((sortingField, index) => {
        sorting[sortingField] = filter.sortingDirections[index];
      });

      return this.subscribePaginatedAggregation(skip, limit, userFilter, [
        {
          $lookup: {
            from: Institution._collectionName,
            as: 'institutions',
            localField: 'roles.contexts',
            foreignField: '_id',
          },
        },
        {
          $addFields: {
            institutionNames: {
              $reduce: {
                input: '$institutions',
                initialValue: '',
                in: { $concat: ['$$value', '$$this.name'] },
              },
            },
          },
        },
        ...(filterObject.search
          ? [
              {
                $match: {
                  $or: [
                    { username: { $regex: filterObject.search, $options: 'i' } },
                    { email: { $regex: filterObject.search, $options: 'i' } },
                    { institutionNames: { $regex: filterObject.search, $options: 'i' } },
                  ],
                },
              },
            ]
          : []),
      ]);
    };
  }

  @Subscription(type => [User])
  public static subscribeUsersPaginated(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => AdornisFilter) filter: AdornisFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      return this.subscribePaginated<User>(skip, limit, filter.getMongoFilter(), filter.getMongoOptions());
    };
  }

  override toString() {
    return `${this.firstName} ${this.lastName} (${this.email})`;
  }

  @Mutation(type => String)
  static updateUserRoles(
    @Arg('userID', type => String) userID: string,
    @Arg('isGlobalAdmin', type => Boolean) isSuperAdmin: boolean,
    @Arg('contexts', type => [String]) contexts: string[],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    return async (gqlFields: void) => {
      if (context.serverContext) return;
      const contextUserID = context.userID as string;
      if (!contextUserID) return;
      const user = await getByID(User, contextUserID)({ roles: { name: 1 } });
      if (!user) throw new Error("current user can't be found");
      const hasSuperAdminPermission = !!user.roles.find(role => role.name === Role.SUPER_ADMIN);
      if (!hasSuperAdminPermission) return;

      const updateUser = await User.getByID<User>(userID)({
        roles: AdornisRoleToContextsWrapper.allFields,
        participations: {
          _id: 1,
          userID: 1,
          productID: 1,
          product: {
            _id: 1,
            name: 1,
            institutionID: 1,
            hasPrice: 1,
            prices: { unitAmount: 1, currency: 1 },
          },
          createdAt: 1,
          updatedAt: 1,
          state: 1,
          isNoShow: 1,
          signoffDate: 1,
          registrationDate: 1,
          fulfilledParticipationRequirements: 1,
          lastStateChangeWasInitiatedByAdmin: 1,
          currentPayment: {
            status: 1,
            participationIDs: 1,
            orderNr: { year: 1, nr: 1 },
            amount: 1,
            discount: 1,
          },
          productPriceForUser: { unitAmount: 1, currency: 1 },
        },
      });

      if (!updateUser) throw new Error('user not found');

      const superAdminRoleIndex = updateUser.roles.findIndex(role => role.name === Role.SUPER_ADMIN);

      if (isSuperAdmin && superAdminRoleIndex === -1)
        updateUser.roles.push(new AdornisRoleToContextsWrapper({ name: Role.SUPER_ADMIN, contexts: [GLOBAL_CONTEXT] }));
      if (!isSuperAdmin && superAdminRoleIndex !== -1) updateUser.roles.splice(superAdminRoleIndex, 1);
      if (!isSuperAdmin) {
        const adminRoleIndex = updateUser.roles.findIndex(role => role.name === Role.ADMIN);
        if (adminRoleIndex !== -1) {
          const role = updateUser.roles.at(adminRoleIndex)!;
          role.contexts = contexts;
        } else {
          const newRole = new AdornisRoleToContextsWrapper({ name: Role.ADMIN, contexts });
          updateUser.roles.push(newRole);
        }
      }

      updateUser.participations = [];
      await updateUser.save();
    };
  }

  static async ensureStripeCustomerForUser(@Arg('userID', type => String) userID: string) {
    const collection = await getRawCollection<User>(this._collectionName);
    const user = await collection.findOne({ _id: userID });
    if (!user) throw new Error('invalid userID');
    if (user.stripeCustomerID) return user.stripeCustomerID;
    const stripeCustomerResponse = await createStripeCustomer({
      email: user.email,
      name: user.firstName + ' ' + user.lastName,
    });
    await collection.updateOne({ _id: userID }, { $set: { stripeCustomerID: stripeCustomerResponse.id } });
    return stripeCustomerResponse.id;
  }

  private static allowedUserCache?: { time: number; userIDs: string[]; contexts: string };
  static async getAllowedUsers() {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({
          _id: 1,
          isAdmin: 1,
          isLecturer: 1,
          adminRole: { contexts: 1 },
          lecturerRole: { contexts: 1 },
          roles: { ...AdornisRoleToContextsWrapper.allFields, contexts: 1 },
        });
    if (!user) return [];

    const userIDs = [user._id];

    // ADMIN
    if (user.isAdmin) {
      const adminContexts = user.adminRole?.contexts ?? [];

      const cacheKey = adminContexts.join('-');
      const cachedData = this.allowedUserCache;
      if (!!cachedData && cachedData.contexts === cacheKey) {
        const diff = Date.now() - cachedData.time;
        if (diff >= 0 && diff < 1000) return cachedData.userIDs;
      }

      const aggregation = [
        { $unwind: '$roles' },
        { $unwind: '$roles.contexts' },
        { $match: { 'roles.contexts': { $in: adminContexts } } },
        { $project: { _id: 1 } },
      ];

      // logger.info({ aggregation }, 'copy me');
      const userCollection = await getRawCollection(User._collectionName);
      const result = await userCollection.aggregate<{ _id: string }>(aggregation).toArray();

      userIDs.push(...result.map(res => res._id));

      this.allowedUserCache = {
        time: Date.now(),
        contexts: cacheKey,
        userIDs,
      };
    }

    if (user.isLecturer) {
      const lecturerContexts = user.lecturerRole?.contexts ?? [];

      // TODO: also filter for workshop
      const aggregation = [
        { $unwind: '$roles' },
        { $unwind: '$roles.contexts' },
        { $match: { 'roles.contexts': { $in: lecturerContexts } } },
        { $project: { _id: 1 } },
      ];

      const userCollection = await getRawCollection(User._collectionName);
      const result = await userCollection.aggregate<{ _id: string }>(aggregation).toArray();

      userIDs.push(...result.map(res => res._id));
    }

    return userIDs;
  }
}

export const getLecturersByInstitutionID = registerQuery({
  operationName: 'getLecturersByInstitutionID',
  type: () => [User],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: (institutionID: string) => {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      const userCollection = await getRawCollection<User>(User._collectionName);
      const result = await userCollection
        .aggregate([
          {
            $addFields: {
              roleNames: {
                $map: {
                  input: '$roles',
                  as: 'role',
                  in: '$$role.name',
                },
              },
            },
          },
          {
            $match: {
              roleNames: { $in: ['lecturer'] },
            },
          },
        ])
        .toArray();

      return result.filter(lecturer => {
        let lecturerRole = lecturer.roles.find(role => role.name == 'lecturer');
        return lecturerRole?.contexts.includes(institutionID);
      });
    };
  },
});

// TODO should this live here or in the Institution File?
export const subscribeInstitutionsByUserId = registerSubscription({
  operationName: 'subscribeInstitutionsByUserId',
  type: () => [Institution],
  params: [{ name: 'userID', type: () => String }],
  resolve: (userID: string) => {
    return async (gqlFields: BaseQLSelectionSet<Institution>) => {
      return await watchQuery(User._collectionName, {
        _id: userID,
      }).pipe(
        map(userDocuments => {
          return userDocuments[0];
        }),
        map(user => {
          const userObj = (user as User) ?? new User({});
          return userObj.roles.filter(r => r.name === Role.LECTURER).flatMap(r => r.contexts);
        }),
        switchMap(async institutionIDs => {
          const selection = {
            name: 1,
          } as const satisfies BaseQLSelectionSet<Institution>;
          return await getByID(Institution, institutionIDs)(selection);
        }),
        // get institutionIds from Roles
        // get Institutions by ID
        // return institutions
      );
    };
  },
});
